import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db91m2jk13ra8mig907g";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // anti-spam honeypot
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam = String(serverMsg ?? "").toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试，或直接发送邮件与我们联系。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="honghu-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">
        在线留言
      </h3>
      <p className="mt-1 text-sm text-foreground-500">
        对内容合作、纠错或投稿有想法？欢迎给我们留言，我们会尽快回复。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cf-name" className="text-sm font-medium text-foreground-700">
            您的称呼
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的称呼"
            className="rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cf-email" className="text-sm font-medium text-foreground-700">
            邮箱地址
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="cf-message" className="text-sm font-medium text-foreground-700">
          留言内容
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入留言内容（最多 500 字）"
          className="resize-none rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
        ></textarea>
      </div>

      <div className="newsletter-field-extra" aria-hidden="true">
        <label htmlFor="cf-website-alt">请勿填写此项</label>
        <input
          id="cf-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "submitting" ? "提交中…" : "提交留言"}
        </button>
        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
            <i className="ri-checkbox-circle-line text-lg"></i>
            提交成功，感谢您的留言！
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-1.5 text-sm font-medium text-primary-700">
            <i className="ri-error-warning-line text-lg"></i>
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}